<template>
  <section>
    <label v-if="isLabel" class="label" :for="id"> {{ t(label) }}</label>

    <div ref="wrapperRef" class="wrap-input validate-input">
      <input
        :id="id"
        ref="inputRef"
        :value="modelValue"
        class="input100"
        :class="{ pr: icon, error }"
        :type="type"
        :placeholder="t(placeholder)"
        :disabled="disabled"
        :readonly="readonly"
        :style="{ height, cursor: cursor }"
        :enterkeyhint="submitOnSearch ? 'search' : undefined"
        @input="handleInput"
        @keydown.enter="handleEnter"
        @blur="handleBlur"
        @focus="showErrorToggle"
      />

      <span class="focus-input100"></span>
      <button
        v-if="icon"
        type="button"
        class="symbol-input100"
        :class="{ clickable: submitOnSearch }"
        :disabled="disabled || !submitOnSearch"
        :tabindex="submitOnSearch ? 0 : -1"
        :aria-label="submitOnSearch ? t(placeholder || 'search') : undefined"
        @click="handleIconClick"
      >
        <font-awesome-icon :icon="icon" />
      </button>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, onMounted, PropType, ref } from 'vue';
import { InputType } from '@/types/input.type';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  props: {
    modelValue: { type: String, default: '' }, // для v-model
    error: { type: String, default: '' }, // ошибка извне (vee-validate)
    isLabel: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    type: {
      type: String as PropType<InputType>,
      default: 'text',
    },
    label: {
      type: String,
      default: 'text',
    },
    icon: {
      type: [Array, String],
      default: '',
    },
    zIndexTooltip: { type: Number, default: 1010 },
    height: {
      type: String as PropType<string>,
      default: '50px',
    },
    isFocused: {
      type: Boolean,
      default: false,
    },
    cursor: {
      type: String,
      default: '',
    },
    /** When true, icon click / Enter emit `search` (for DB queries) instead of searching on every keystroke. */
    submitOnSearch: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'blur', 'change', 'focus', 'search'],
  setup(props, { emit }) {
    const id = Math.random();
    const value = ref('');
    const { t } = useI18n();
    const inputRef = ref<HTMLInputElement | null>(null);
    const wrapperRef = ref<HTMLElement | null>(null);

    const showErrorToggle = () => {
      emit('focus');
    };

    const handleBlur = () => {
      emit('blur');
    };

    const handleInput = (e: Event) => {
      const next = (e.target as HTMLInputElement).value;
      emit('update:modelValue', next);
      // Cleared field → search immediately (no icon / Enter needed).
      if (props.submitOnSearch && !String(next || '').trim()) {
        emit('search', next);
      }
    };

    const emitSearch = () => {
      if (!props.submitOnSearch) return;
      emit('search', props.modelValue ?? '');
    };

    const handleEnter = (e: KeyboardEvent) => {
      if (!props.submitOnSearch) return;
      e.preventDefault();
      emitSearch();
    };

    const handleIconClick = () => {
      if (!props.submitOnSearch) return;
      emitSearch();
    };

    onMounted(() => {
      if (props.isFocused) {
        inputRef.value?.focus();
      }
    });

    return {
      value,
      showErrorToggle,
      inputRef,
      wrapperRef,
      handleInput,
      handleBlur,
      handleEnter,
      handleIconClick,
      t,
      id,
    };
  },
});
</script>
<style scoped lang="scss">
.label {
  display: block;
  text-align: left;
  padding: 0 1rem 0.3rem 1rem;
  font-weight: bold;
  font-size: 14px;
}
.wrap-input {
  position: relative;
}

input {
  outline: none;
  border: none;
}

.input100 {
  font-family: Poppins-Medium, sans-serif;
  font-size: 17px;
  line-height: 1.5;
  color: #666;
  display: block;
  width: 100%;
  background: #e6e6e6;
  border-radius: 25px;
  padding: 0 30px 0 20px;
  position: relative;
  z-index: 1;
}
.input100.pr {
  padding: 0 30px 0 68px;
}
/* Подложка для свечения */
.focus-input100 {
  display: block;
  position: absolute;
  border-radius: 25px;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.symbol-input100 {
  font-size: 15px;
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  border: 0;
  background: transparent;
  border-radius: 25px;
  bottom: 0;
  left: 0;
  width: 56px;
  height: 100%;
  padding: 0;
  margin: 0;
  pointer-events: none;
  color: #666666;
  -webkit-transition: all 0.4s;
  -o-transition: all 0.4s;
  -moz-transition: all 0.4s;
  transition: all 0.4s;
  z-index: 2;
}

.symbol-input100.clickable {
  pointer-events: auto;
  cursor: pointer;
}

.symbol-input100.clickable:disabled {
  cursor: default;
}

.error {
  border: 1px solid red;
}

/* Запуск анимации при фокусе */
:deep(.input100:focus + .focus-input100) {
  animation: glow-fade 0.8s ease-in-out forwards;
}

:deep(.input100:focus) ~ .symbol-input100 {
  color: #57b846;
}

/* Кадры анимации */
@keyframes glow-fade {
  0% {
    box-shadow: 0 0 0 0 rgba(87, 184, 70, 0);
  }
  10% {
    box-shadow: 0 0 25px 10px rgba(87, 184, 70, 1); /* пик яркости */
  }
  100% {
    box-shadow: 0 0 0 0 rgba(87, 184, 70, 0); /* полное затухание */
  }
}

/* Плавное исчезновение placeholder */
.input100:focus::placeholder {
  color: transparent;
  transition: color 0.3s ease;
}
.validate-input {
  position: relative;
}

.input100:disabled {
  background-color: #f0f0f0;
  color: #999;
  opacity: 0.5;
}

.input100::placeholder {
  text-transform: lowercase;
}
</style>
